// frontend/src/api/confirmations.api.ts
import api from './axios';

export interface ConfirmationGuest {
  guest_name: string;
  passport_number?: string;
  passport_country?: string;
  phone?: string;
  email?: string;
  sort_order?: number;
}

export interface ConfirmationChild {
  child_name: string;
  child_age?: number;
  sort_order?: number;
}

export interface ConfirmationProperty {
  id: number;
  property_name: string;
  property_number: string;
  address: string;
  region: string;
  bedrooms: number;
  property_type: string;
  complex_name: string;
}

export interface FieldVisibility {
  [key: string]: boolean;
}

export interface Confirmation {
  id: number;
  confirmation_number: string;
  document_sequence?: number;
  property_id?: number;
  template_id?: number;
  property_name?: string;
  property_number?: string;
  property_address?: string;
  from_company_name?: string;
  from_telephone?: string;
  from_email?: string;
  confirmation_date?: string;
  arrival_date?: string;
  departure_date?: string;
  arrival_time?: string;
  departure_time?: string;
  check_in_time?: string;
  check_out_time?: string;
  room_type?: string;
  rate_type?: string;
  rate_amount?: number;
  num_rooms?: number;
  num_guests?: number;
  num_children?: number;
  deposit_amount?: number;
  currency?: string;
  pdf_version?: number;
  pick_up_service?: string;
  drop_off_service?: string;
  arrival_flight?: string;
  departure_flight?: string;
  remarks?: string;
  notice_content?: string;
  cancellation_policy?: string;
  policy_template_key?: string;
  payment_terms_option?: string;
  payment_terms_text?: string;
  payment_deadline?: string;
  payment_status_option?: string;
  payment_status_text?: string;
  payment_notice_type?: string;
  payment_notice_date?: string;
  payment_notice_text?: string;
  welcome_message?: string;
  included_services?: string[];
  electricity_rate?: number;
  water_rate?: number;
  field_visibility?: FieldVisibility;
  total_amount?: number;
  status?: string;
  payment_status?: string;
  agent_name?: string;
  channel?: string;
  created_by?: number;
  created_at?: string;
  updated_at?: string;
  guests?: ConfirmationGuest[];
  children?: ConfirmationChild[];
  guests_count?: number;
  total_expected?: number;
  total_room_fees?: number;
  total_paid?: number;
  next_payment_date?: string;
  next_payment_amount?: number;
}

export interface CreateConfirmationDTO {
  property_id?: number;
  template_id?: number;
  property_name?: string;
  property_address?: string;
  from_company_name?: string;
  from_telephone?: string;
  from_email?: string;
  confirmation_date?: string;
  arrival_date?: string;
  departure_date?: string;
  arrival_time?: string;
  departure_time?: string;
  check_in_time?: string;
  check_out_time?: string;
  room_type?: string;
  rate_type?: string;
  rate_amount?: number;
  num_rooms?: number;
  num_guests?: number;
  num_children?: number;
  deposit_amount?: number;
  currency?: string;
  pdf_version?: number;
  pick_up_service?: string;
  drop_off_service?: string;
  arrival_flight?: string;
  departure_flight?: string;
  remarks?: string;
  notice_content?: string;
  cancellation_policy?: string;
  policy_template_key?: string;
  payment_terms_option?: string;
  payment_terms_text?: string;
  payment_deadline?: string;
  payment_status_option?: string;
  payment_status_text?: string;
  payment_notice_type?: string;
  payment_notice_date?: string;
  payment_notice_text?: string;
  welcome_message?: string;
  included_services?: string[];
  electricity_rate?: number;
  water_rate?: number;
  field_visibility?: FieldVisibility;
  payment_status?: string;
  agent_name?: string;
  channel?: string;
  guests?: ConfirmationGuest[];
  children?: ConfirmationChild[];
}

export interface ConfirmationPayment {
  id: number;
  confirmation_id: number;
  amount: number;
  currency: string;
  payment_date: string;
  payment_method: string;
  notes?: string;
  created_by?: number;
  created_by_name?: string;
  created_at?: string;
  updated_at?: string;
  files?: ConfirmationPaymentFile[];
}

export interface ConfirmationPaymentFile {
  id: number;
  payment_id: number;
  file_path: string;
  file_name: string;
  file_size: number;
  mime_type?: string;
  uploaded_at?: string;
}

export interface ConfirmationReminder {
  id: number;
  confirmation_id: number;
  reminder_type: 'check_in_day' | 'days_before' | 'custom_date';
  days_before?: number;
  custom_date?: string;
  comment?: string;
  recurrence_type: string;
  recurrence_interval?: number;
  recurrence_day_of_week?: number;
  recurrence_week_of_month?: number;
  recurrence_end_date?: string;
  recurrence_max_count?: number;
  recurrence_sent_count?: number;
  next_send_date?: string;
  is_enabled: boolean;
  is_sent: boolean;
  sent_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ExpectedAmount {
  id?: number;
  amount: number;
  currency: string;
  fee_type?: string;
}

export interface CurrencyPaymentStatus {
  currency: string;
  rent?: number;
  room?: number;
  expected: number;
  paid: number;
  remaining: number;
  is_fully_paid: boolean;
}

export interface PaymentSummary {
  expected_amounts: ExpectedAmount[];
  currency_statuses: CurrencyPaymentStatus[];
  payment_status: string;
  payments_count: number;
}

export const confirmationsApi = {
  // CRUD
  getAll: () => api.get('/confirmations'),
  getById: (id: number) => api.get(`/confirmations/${id}`),
  create: (data: CreateConfirmationDTO) => api.post('/confirmations', data),
  update: (id: number, data: CreateConfirmationDTO) => api.put(`/confirmations/${id}`, data),
  delete: (id: number) => api.delete(`/confirmations/${id}`),
  getProperties: () => api.get('/confirmations/properties/list'),

  // Payments
  getPaymentSummary: (id: number) => api.get(`/confirmations/${id}/payment-summary`),
  getExpectedAmounts: (id: number) => api.get(`/confirmations/${id}/expected-amounts`),
  updateExpectedAmounts: (id: number, expected_amounts: ExpectedAmount[]) => api.put(`/confirmations/${id}/expected-amounts`, { expected_amounts }),
  getPayments: (id: number) => api.get(`/confirmations/${id}/payments`),
  createPayment: (id: number, data: any) => api.post(`/confirmations/${id}/payments`, data),
  updatePayment: (id: number, paymentId: number, data: any) => api.put(`/confirmations/${id}/payments/${paymentId}`, data),
  deletePayment: (id: number, paymentId: number) => api.delete(`/confirmations/${id}/payments/${paymentId}`),
  uploadPaymentFiles: (id: number, paymentId: number, formData: FormData) => api.post(`/confirmations/${id}/payments/${paymentId}/upload-files`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deletePaymentFile: (id: number, paymentId: number, fileId: number) => api.delete(`/confirmations/${id}/payments/${paymentId}/files/${fileId}`),

  // Reminders
  getReminders: (id: number) => api.get(`/confirmations/${id}/reminders`),
  createReminder: (id: number, data: any) => api.post(`/confirmations/${id}/reminders`, data),
  updateReminder: (id: number, reminderId: number, data: any) => api.put(`/confirmations/${id}/reminders/${reminderId}`, data),
  deleteReminder: (id: number, reminderId: number) => api.delete(`/confirmations/${id}/reminders/${reminderId}`),

  // PDF
  generatePDF: (id: number) => api.post(`/confirmations/${id}/generate-pdf`),
  downloadPDF: (id: number) => api.get(`/confirmations/${id}/pdf`, { responseType: 'blob' }),

  // Payment Schedule
  getPaymentSchedule: (id: number) => api.get(`/confirmations/${id}/payment-schedule`),
  createScheduleItem: (id: number, data: any) => api.post(`/confirmations/${id}/payment-schedule`, data),
  updateScheduleItem: (id: number, itemId: number, data: any) => api.put(`/confirmations/${id}/payment-schedule/${itemId}`, data),
  deleteScheduleItem: (id: number, itemId: number) => api.delete(`/confirmations/${id}/payment-schedule/${itemId}`),
  markSchedulePaid: (id: number, itemId: number) => api.put(`/confirmations/${id}/payment-schedule/${itemId}/mark-paid`),
  markScheduleUnpaid: (id: number, itemId: number) => api.put(`/confirmations/${id}/payment-schedule/${itemId}/mark-unpaid`),
  uploadScheduleSlip: (id: number, itemId: number, formData: FormData) => api.post(`/confirmations/${id}/payment-schedule/${itemId}/upload-slip`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export interface PaymentScheduleItem {
  id: number;
  confirmation_id: number;
  due_date: string;
  amount: number;
  currency: string;
  description?: string;
  is_paid: boolean;
  paid_date?: string;
  payment_id?: number;
  reminder_enabled: boolean;
  reminder_days_before: number;
  reminder_sent: boolean;
  next_reminder_date?: string;
}
